import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";

Vue.use(Vuex);

const heightBreakpoint = 980;

export default new Vuex.Store({
  state: {
    entryId: null,
    loaded: false,
    timelineTransitioning: false,
    selectedDecade: 0,
    decadeLoading: false,
    decadeTransitioning: false,
    items: [],
    scrolling: false,
    currentAnchorIndex: 0,
    scrollThreshold: 0,
    decades: [],
    expandAnnualMessage: false,
    annualMessageScrollTop: 0,
    containerHeight: 0,
    containerScrollTop: 0,
    windowHeight: 0,
    windowWidth: 0,
    isMobile: false,
    expandNav: false,
  },
  getters: {
    isMobile: state => state.isMobile,
    decades: state => state.decades,
    loaded: state => state.loaded,
    timelineTransitioning: state => state.timelineTransitioning,
    scrolling: state => state.scrolling,
    selectedDecade: state => state.selectedDecade,
    items: state => state.items,
    numItems: state => state.items.length,
    currentAnchorIndex: state => state.currentAnchorIndex,
    scrollThreshold: state => state.scrollThreshold,
    decadeLoading: state => state.decadeLoading,
    decadeTransitioning: state => state.decadeTransitioning,
    expandIntro: state => state.expandAnnualMessage,
    annualMessageScrollTop: state => state.annualMessageScrollTop,
    containerHeight: state => state.containerHeight,
    containerScrollTop: state => state.containerScrollTop,
    windowHeight: state => state.windowHeight,
    windowWidth: state => state.windowWidth,
    expandNav: state => state.expandNav,
    decadeBarsInitialHeight: state => (state.windowHeight >= heightBreakpoint ? 28 : 22),
    decadeBarsFinalHeight: state => (state.windowHeight >= heightBreakpoint ? 15 : 12),
    aboveTheFoldScrollPercentage: (state, getters) => {
      return getters.containerHeight > 0 ? Math.min(state.annualMessageScrollTop / getters.containerHeight, 1) : 0;
    },
    belowTheFoldScrollPercentage: (state, getters) => {
      return getters.containerHeight > 0 ? Math.min(Math.max(state.annualMessageScrollTop / getters.containerHeight - 1, 0), 1) : 0;
    },
  },
  mutations: {
    setEntryId: (state, entryId) => { state.entryId = entryId; },
    setLoaded: (state, loaded) => state.loaded = loaded,
    setTimelineTransitioning: (state, transitioning) => state.timelineTransitioning = transitioning,
    setDecade: (state, decade) => state.selectedDecade = decade,
    setDecadeLoading: (state, loading) => state.decadeLoading = loading,
    setDecadeTransitioning: (state, transitioning) => state.decadeTransitioning = transitioning,
    setItems: (state, items) => state.items = items,
    setScrolling: (state, scrolling) => state.scrolling = scrolling,
    setCurrentAnchorIndex: (state, index) => state.currentAnchorIndex = index,
    setScrollThreshold: (state, threshold) => state.scrollThreshold = threshold,
    setExpandIntro: (state, expand) => state.expandAnnualMessage = expand,
    setIntroScrollTop: (state, scrollTop) => state.annualMessageScrollTop = scrollTop,
    setContainerHeight: (state, height) => state.containerHeight = height,
    setContainerScrollTop: (state, scrollTop) => state.containerScrollTop = scrollTop,
    setWindowHeight: (state, height) => state.windowHeight = height,
    setWindowWidth: (state, width) => state.windowWidth = width,
    setExpandNav: (state, expand) => state.expandNav = expand,
    setIsMobile: (state, isMobile) => state.isMobile = isMobile,
    setDecades: (state, decades) => state.decades = decades,
  },
  actions: {
    setLoaded: ({ commit }, loaded) => commit("setLoaded", loaded),
    selectDecade({ commit, state, dispatch }, decade) {
      if (!state.entryId) {
        console.error("entryId is not set in state");
        return;
      }
      decade = parseInt(decade);
      if (!state.decades.includes(decade)) decade = 0;
      window.location.href = decade !== 0 ? `#${decade}s` : "#";
      if (state.isMobile) return dispatch("selectDecadeMobile", decade);
      if (decade === state.selectedDecade || state.decadeLoading) return;
      let isInitialTransition = state.selectedDecade === 0;
      if (isInitialTransition) {
        commit("setTimelineTransitioning", "in");
        setTimeout(() => commit("setTimelineTransitioning", false), 1950);
      }
      if (decade === 0) {
        commit("setTimelineTransitioning", "out");
        commit("setDecade", decade);
        commit("setIntroScrollTop", 0);
        setTimeout(() => commit("setTimelineTransitioning", false), 1950);
        return;
      }
      let startTime = Date.now();
      commit("setDecade", decade);
      commit("setDecadeLoading", true);
      setTimeout(() => {
        commit("setDecadeTransitioning", true);
        commit("setCurrentAnchorIndex", 0);
      }, CONFIG.ANIMATION_SPEED);
      axios.get("/actions/my-module/timeline/timeline", { params: { decade, entryId: state.entryId } })
        .then(response => {
          let items = response.data;
          if (!Array.isArray(items)) {
            items = Object.values(items);
          }
          console.log("Fetched items for decade:", decade, items);
          commit("setItems", items);
          let endTime = Date.now();
          let timeoutDuration = Math.max(CONFIG.ANIMATION_SPEED * 3 - (endTime - startTime), 0);
          setTimeout(() => {
            commit("setDecadeLoading", false);
            commit("setDecadeTransitioning", false);
            dispatch("setCurrentAnchorIndex", 0);
          }, timeoutDuration);
        })
        .catch(error => {
          commit("setDecadeLoading", false);
          commit("setDecadeTransitioning", false);
        });
    },
    selectDecadeMobile({ commit, state }, decade) {
      if (!state.entryId) {
        console.error("entryId is not set in state");
        return;
      }
      commit("setDecade", decade);
      commit("setExpandNav", false);
      if (decade === 0) return;
      let startTime = Date.now();
      commit("setDecadeLoading", true);
      axios.get("/actions/my-module/timeline/timeline", { params: { decade, entryId: state.entryId } })
        .then(response => {
          commit("setItems", response.data);
          let endTime = Date.now();
          let timeoutDuration = Math.max(CONFIG.ANIMATION_SPEED - (endTime - startTime), 0);
          setTimeout(() => commit("setDecadeLoading", false), timeoutDuration);
        });
    },
    setScrolling: ({ commit }, scrolling) => commit("setScrolling", scrolling),
    setCurrentAnchorIndex({ commit }, index) {
      commit("setScrolling", true);
      commit("setScrollThreshold", 0);
      commit("setCurrentAnchorIndex", index);
      setTimeout(() => {
        commit("setScrolling", false);
        commit("setScrollThreshold", 0);
      }, CONFIG.ANIMATION_SPEED * 1.5);
    },
    setScrollThreshold: ({ commit }, threshold) => commit("setScrollThreshold", threshold),
    setExpandIntro: ({ commit }, expand) => commit("setExpandIntro", expand),
    setIntroScrollTop: ({ commit }, scrollTop) => commit("setIntroScrollTop", scrollTop),
    setContainerHeight: ({ commit }, height) => commit("setContainerHeight", height),
    setContainerScrollTop: ({ commit }, scrollTop) => commit("setContainerScrollTop", scrollTop),
    setWindowHeight: ({ commit }, height) => commit("setWindowHeight", height),
    setWindowWidth: ({ commit }, width) => commit("setWindowWidth", width),
    setExpandNav: ({ commit }, expand) => commit("setExpandNav", expand),
    setIsMobile: ({ commit }, isMobile) => commit("setIsMobile", isMobile),
    fetchDecades: ({ commit, state }) => {
      if (!state.entryId) {
        console.error("entryId is not set in state");
        return;
      }
      axios.get("/actions/my-module/timeline/decades", { params: { entryId: state.entryId } })
        .then(response => commit("setDecades", response.data))
        .catch(error => console.error("Error fetching decades:", error));
    },
    fetchIntroData({ state }, entryId) {
      return axios.get(`/actions/my-module/timeline/intro`, { params: { entryId } })
        .then(response => response.data)
        .catch(error => {
          console.error("Error fetching intro data:", error);
          throw error;
        });
    }
  },
});


// WEBPACK FOOTER //
// ./timeline/js/components/store.js